/**
 * LAYOUT SYSTEM INVARIANTS
 *
 * These rules are immutable. Violating them breaks the system.
 */

import type { Vector3D, LayoutNode, LayoutAxis, LayoutContainer, LayoutItem } from './types';
import { Logger } from '../utils/Logger';

// === ASSERTION HELPERS ===

/**
 * Assert that a resolved size respects minimum constraints.
 */
export function assertMinSize(resolved: Vector3D, min: Vector3D, nodeId: string): void {
  if (resolved.x < min.x || resolved.y < min.y || resolved.z < min.z) {
    throw new Error(
      `Layout invariant violated: Node ${nodeId} resolvedSize (${resolved.x}, ${resolved.y}, ${resolved.z}) ` +
      `is below minSize (${min.x}, ${min.y}, ${min.z})`
    );
  }
}

/**
 * Assert that a vector contains no NaN or negative values.
 */
export function assertValidDimensions(vec: Vector3D, context: string): void {
  if (isNaN(vec.x) || isNaN(vec.y) || isNaN(vec.z)) {
    throw new Error(`Layout invariant violated: NaN in ${context}: (${vec.x}, ${vec.y}, ${vec.z})`);
  }
  if (vec.x < 0 || vec.y < 0 || vec.z < 0) {
    throw new Error(`Layout invariant violated: Negative dimension in ${context}: (${vec.x}, ${vec.y}, ${vec.z})`);
  }
}

/**
 * Assert that a position contains finite values.
 */
export function assertFinitePosition(pos: Vector3D, nodeId: string): void {
  if (!isFinite(pos.x) || !isFinite(pos.y) || !isFinite(pos.z)) {
    throw new Error(
      `Layout invariant violated: Non-finite position for node ${nodeId}: (${pos.x}, ${pos.y}, ${pos.z})`
    );
  }
}

/**
 * Run all layout invariant checks on a node after layout computation.
 */
export function validateLayoutNode(node: LayoutNode): void {
  assertValidDimensions(node.resolvedSize, `node ${node.id} resolvedSize`);
  assertMinSize(node.resolvedSize, node.layoutBox.min, node.id);
  assertFinitePosition(node.resolvedPosition, node.id);
}

// === AXIS HELPERS ===

/**
 * Reports already made, keyed by the whole message, so an out-of-vocabulary
 * axis in a hot layout pass says its piece once rather than once per node per
 * solve. Keyed by message rather than by value because the same bad value
 * reaches these helpers through different fields with different consequences,
 * and each of those is worth hearing once.
 */
const reportedUnknownAxes = new Set<string>();

/** Test seam: forget which unknown axis values have already been reported. */
export function resetUnknownAxisReports(): void {
  reportedUnknownAxes.clear();
}

/**
 * Say once that an axis was not in the vocabulary — the field it came in on,
 * the value, and what this helper did instead.
 *
 * Every axis switch here is total, and each one degrades rather than throws.
 * That is only safe while it is also LOUD: a silent wrong answer (children
 * quietly stacked at the origin because a write went nowhere) is worse to
 * chase than a crash, so the fallback is always reported.
 */
function reportUnknownAxis(field: string, axis: LayoutAxis, consequence: string): void {
  const message =
    `[Layout] Unknown ${field} "${axis}" — expected 'X', 'Y' or 'Z'. ${consequence}`;
  if (reportedUnknownAxes.has(message)) return;
  reportedUnknownAxes.add(message);
  Logger.warn(message);
}

const ALIGNMENT_WORDS: Record<string, string> = {
  start: 'Start', 'flex-start': 'Start', left: 'Start', top: 'Start',
  center: 'Center',
  end: 'End', 'flex-end': 'End', right: 'End', bottom: 'End',
  stretch: 'Stretch',
  spacebetween: 'SpaceBetween', 'space-between': 'SpaceBetween',
  spacearound: 'SpaceAround', 'space-around': 'SpaceAround',
  spaceevenly: 'SpaceEvenly', 'space-evenly': 'SpaceEvenly',
};

/**
 * Read an authored alignment as one the offset helpers know.
 *
 * Alignment is authored data, and a generated payload spells it CSS-style
 * (`'center'`, `'flex-end'`). The helpers that turn it into an offset are
 * switches with no default, so an unrecognised value used to come back as
 * `undefined` and poison the child's position with NaN, which fails the
 * finite-position invariant and skips the whole pass. Case and the usual CSS
 * spellings are read as what they mean; anything else degrades to `fallback`
 * and is reported once.
 */
export function normalizeAlignment<T extends string>(
  value: T | undefined,
  allowed: readonly string[],
  fallback: T,
  field: string,
): T {
  if (value === undefined || value === null) return fallback;
  if (allowed.includes(value)) return value;
  const mapped = typeof value === 'string' ? ALIGNMENT_WORDS[value.trim().toLowerCase()] : undefined;
  if (mapped !== undefined && allowed.includes(mapped)) return mapped as T;
  const message = `[Layout] Unknown ${field} "${value}" — using '${fallback}'.`;
  if (!reportedUnknownAxes.has(message)) {
    reportedUnknownAxes.add(message);
    Logger.warn(message);
  }
  return fallback;
}

const CROSS_ALIGNMENTS = ['Start', 'Center', 'End', 'Stretch'] as const;
const MAIN_ALIGNMENTS = ['Start', 'Center', 'End', 'SpaceBetween', 'SpaceAround', 'SpaceEvenly'] as const;

/** Make a container's authored alignments ones the offset helpers know. The engine calls this on every node before it solves, so everything that reads alignment off the tree (the offset helpers, the behavior's wrap feed) sees the canonical word. */
export function normalizeContainerAlignments(c: LayoutContainer): void {
  c.alignMain = normalizeAlignment(c.alignMain, MAIN_ALIGNMENTS, 'Start', 'alignMain');
  c.alignCrossA = normalizeAlignment(c.alignCrossA, CROSS_ALIGNMENTS, 'Start', 'alignCross');
  c.alignCrossB = normalizeAlignment(c.alignCrossB, CROSS_ALIGNMENTS, 'Start', 'alignCrossB');
  if (c.alignContentCrossA !== undefined) c.alignContentCrossA = normalizeAlignment(c.alignContentCrossA, MAIN_ALIGNMENTS, 'Start', 'alignContent');
  if (c.alignContentCrossB !== undefined) c.alignContentCrossB = normalizeAlignment(c.alignContentCrossB, MAIN_ALIGNMENTS, 'Start', 'alignContentCrossB');
}

/** Same for a child's own cross alignment; unset stays unset (it inherits the container). */
export function normalizeItemAlignments(item: LayoutItem): void {
  if (item.alignSelfCross !== undefined) item.alignSelfCross = normalizeAlignment(item.alignSelfCross, CROSS_ALIGNMENTS, 'Start', 'alignSelfCross');
  if (item.alignSelfCrossB !== undefined) item.alignSelfCrossB = normalizeAlignment(item.alignSelfCrossB, CROSS_ALIGNMENTS, 'Start', 'alignSelfCrossB');
}

/**
 * Get the cross axes for a given main axis.
 *
 * TOTAL BY CONSTRUCTION. `directionAxis` is authored data — it reaches the
 * solver from a `.trace` file, a script, or a generated payload, and nothing
 * upstream guarantees it spells an axis. A CSS-flavoured `'vertical'` used to
 * fall off the end of this switch and return `undefined`, which the
 * destructuring at every call site turned into a TypeError inside a deferred
 * solve — i.e. a dead session for the host. An unrecognised value now degrades
 * to the X-axis pair (`['Y', 'Z']`, what `getCrossAxes('X')` returns) and is
 * reported once through the engine's Logger, naming the field and the value.
 * Never throws.
 */
export function getCrossAxes(mainAxis: LayoutAxis): [LayoutAxis, LayoutAxis] {
  switch (mainAxis) {
    case 'X': return ['Y', 'Z'];
    case 'Y': return ['X', 'Z'];
    case 'Z': return ['X', 'Y'];
    default:
      reportUnknownAxis('directionAxis', mainAxis, `Falling back to the X-axis cross pair ['Y', 'Z'].`);
      return ['Y', 'Z'];
  }
}

/**
 * Cross axes for a container, honoring an authored wrapAxis: when it names
 * the second cross axis, the roles swap — lines stack (and crossGap and
 * alignContent apply) along the chosen axis, layers along the other. Any
 * other value falls back to the default order, so containers without a
 * valid wrapAxis behave exactly as before. wrapAxis is wrap-only by
 * definition (it names the axis wrapped LINES travel along), so a non-wrap
 * container never swaps — cross-axis roles on a stray authored wrapAxis
 * stay exactly as they were before wrapAxis existed.
 */
export function getContainerCrossAxes(
  container: { directionAxis: LayoutAxis; wrap?: boolean; wrapAxis?: LayoutAxis },
): [LayoutAxis, LayoutAxis] {
  const [crossA, crossB] = getCrossAxes(container.directionAxis);
  return container.wrap === true && container.wrapAxis === crossB
    ? [crossB, crossA]
    : [crossA, crossB];
}

/**
 * Get a component of a Vector3D by axis name.
 *
 * An unrecognised axis reads no component: the answer is `NaN`, the
 * arithmetic downstream stays `NaN`, and the engine's own invariant check
 * reports it. That is already loud; what was missing is WHICH axis value
 * caused it, which is what the report adds.
 */
export function getAxisValue(vec: Vector3D, axis: LayoutAxis): number {
  switch (axis) {
    case 'X': return vec.x;
    case 'Y': return vec.y;
    case 'Z': return vec.z;
    default:
      reportUnknownAxis('axis', axis, 'Reading no component — the arithmetic downstream goes NaN.');
      return NaN;
  }
}

/**
 * Set a component of a Vector3D by axis name (immutable).
 *
 * An unrecognised axis writes nothing and returns the vector unchanged, as it
 * always has. This is the quietest of the three and so the most dangerous: a
 * dropped write produces no NaN and trips no invariant — the solve simply
 * finishes with every child sitting where it started, which reads as a layout
 * bug rather than as bad data. It now says so.
 */
export function setAxisValue(vec: Vector3D, axis: LayoutAxis, value: number): Vector3D {
  const result = { ...vec };
  switch (axis) {
    case 'X': result.x = value; break;
    case 'Y': result.y = value; break;
    case 'Z': result.z = value; break;
    default:
      reportUnknownAxis('axis', axis, 'Writing no component — the vector is returned unchanged.');
      break;
  }
  return result;
}
